import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useRegisterRefresh } from '../context/PullToRefreshContext';

function StatusBadge({ status, isOverdue }) {
  if (isOverdue || status === 'overdue') return <span className="badge-overdue">Süresi Geçmiş</span>;
  if (status === 'completed') return <span className="badge-completed">Tamamlandı</span>;
  return <span className="badge-in-progress">Devam Ediyor</span>;
}

function FileIcon() {
  return (
    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ProcessNote({ note, canManage, canEdit, isAdmin, onRefresh }) {
  const [playing, setPlaying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(note.text || '');
  const [editSaving, setEditSaving] = useState(false);

  const audioUrl = note.audio_file
    ? `${process.env.REACT_APP_API_URL || 'http://localhost:8002'}${note.audio_file}`
    : null;

  const handleDeleteImage = async (imageId) => {
    await api.deleteNoteImage(imageId);
    onRefresh();
  };

  const handleDeleteFile = async (fileId) => {
    await api.deleteNoteFile(fileId);
    onRefresh();
  };

  const handleAdminDeleteNote = async () => {
    await api.adminDeleteNote(note.id);
    onRefresh();
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      await api.updateNote(note.id, editText);
      setEditing(false);
      onRefresh();
    } catch { /* sessiz */ }
    finally { setEditSaving(false); }
  };

  const isEmpty = !note.text && !note.has_audio && !(note.images?.length > 0) && !(note.files?.length > 0);

  return (
    <div className="border-l-2 border-brand-200 pl-3 py-1">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-gray-500 mb-1">
          <span className="font-medium text-gray-700">{note.added_by_name}</span> tarafından eklendi
          <span className="ml-1">• {format(new Date(note.created_at), 'd MMMM yyyy HH:mm', { locale: tr })}</span>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => { setEditText(note.text || ''); setEditing(true); }}
              className="text-xs text-brand-500 hover:text-brand-700 font-medium"
              title="Notu düzenle"
            >
              Düzenle
            </button>
          )}
          {isAdmin && isEmpty && (
            <button
              type="button"
              onClick={handleAdminDeleteNote}
              className="text-xs text-red-400 hover:text-red-600 font-medium"
              title="Boş notu kaldır"
            >
              Kaldır
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2 mt-1">
          <textarea
            className="input min-h-[80px] resize-none text-sm"
            value={editText}
            onChange={e => setEditText(e.target.value)}
            autoFocus
          />
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
              onClick={handleSaveEdit}
              disabled={editSaving}
            >
              {editSaving ? '…' : 'Kaydet'}
            </button>
            <button
              type="button"
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg"
              onClick={() => setEditing(false)}
            >
              İptal
            </button>
          </div>
        </div>
      ) : (
        note.text && <p className="text-sm text-gray-700">{note.text}</p>
      )}
      {note.has_audio && audioUrl && (
        <>
          <button
            type="button"
            className="mt-1 flex items-center gap-1 text-xs text-brand-600 font-medium"
            onClick={() => setPlaying(p => !p)}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            {playing ? 'Durdur' : 'Ses Kaydını Dinle'}
          </button>
          {playing && (
            <audio
              src={audioUrl}
              autoPlay
              controls
              className="mt-2 w-full"
              onEnded={() => setPlaying(false)}
            />
          )}
        </>
      )}
      {(note.images?.length > 0 || note.deleted_images_count > 0) && (
        <div className="mt-2 flex flex-col gap-2">
          {note.images.map((img) => (
            <div key={img.id} className="relative">
              <img
                src={img.url}
                alt=""
                className="rounded-lg max-w-full"
                style={{ display: 'block' }}
              />
              {canManage && (
                <button
                  type="button"
                  onClick={() => handleDeleteImage(img.id)}
                  className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center text-xs leading-none"
                  title="Görseli sil"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {note.deleted_images_count > 0 && (
            <p className="text-xs text-gray-400 italic">
              {note.deleted_images_count} görsel silindi
            </p>
          )}
        </div>
      )}
      {note.files?.length > 0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {note.files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
              <FileIcon />
              <a
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 text-xs text-brand-600 font-medium truncate hover:underline"
              >
                {f.original_name}
              </a>
              {canManage && (
                <button
                  type="button"
                  onClick={() => handleDeleteFile(f.id)}
                  className="shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                  title="Dosyayı sil"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // Modal state'leri
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteError, setNoteError] = useState('');
  const [delegateId, setDelegateId] = useState('');
  const [delegateNote, setDelegateNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [noteImages, setNoteImages] = useState([]);
  const [noteFiles, setNoteFiles] = useState([]);

  // Süreç notu modalı — mail ile özet
  const [showMailInput, setShowMailInput] = useState(false);
  const [mailText, setMailText] = useState('');
  const [mailSummarizing, setMailSummarizing] = useState(false);
  const [mailError, setMailError] = useState('');

  // Deadline revize modal state'leri
  const [showDeadlineModal, setShowDeadlineModal] = useState(false);
  const [newDeadline, setNewDeadline] = useState('');
  const [deadlineReason, setDeadlineReason] = useState('');
  const [deadlineSaving, setDeadlineSaving] = useState(false);
  const [deadlineError, setDeadlineError] = useState('');

  // Kategori değiştirme state'leri
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [catType, setCatType] = useState('');
  const [catId, setCatId] = useState('');
  const [catCustomerId, setCatCustomerId] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState('');
  // Yeni kategori talep alt formu
  const [showNewCatForm, setShowNewCatForm] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatSubmitting, setNewCatSubmitting] = useState(false);
  const [newCatMessage, setNewCatMessage] = useState('');
  const [newCatMessageType, setNewCatMessageType] = useState('info');

  const [showAdminDeleteModal, setShowAdminDeleteModal] = useState(false);
  const [adminDeleteSaving, setAdminDeleteSaving] = useState(false);

  // Paralel süreçler
  const [subprocesses, setSubprocesses] = useState([]);
  const [activeSpId, setActiveSpId] = useState(null);
  const [showNewSpModal, setShowNewSpModal] = useState(false);
  const [newSpTitle, setNewSpTitle] = useState('');
  const [newSpDeadline, setNewSpDeadline] = useState('');
  const [newSpSaving, setNewSpSaving] = useState(false);
  const [newSpError, setNewSpError] = useState('');
  const [spNoteText, setSpNoteText] = useState('');
  const [spNoteError, setSpNoteError] = useState('');
  const [spNoteSaving, setSpNoteSaving] = useState(false);

  // Başlık düzenleme
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);
  const [titleError, setTitleError] = useState('');

  const refreshTask = useCallback(() => {
    api.getTask(id).then(({ data }) => setTask(data));
  }, [id]);

  const refreshSubProcesses = useCallback(() => {
    api.getSubProcesses(id)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data?.results || []);
        setSubprocesses(list);
      })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    setSpNoteText('');
    setSpNoteError('');
  }, [activeSpId]);

  useEffect(() => {
    api.getTask(id).then(({ data }) => { setTask(data); setLoading(false); });
    api.getUsers().then(({ data }) => setUsers(Array.isArray(data) ? data : data.results || []));
    api.getSubProcesses(id)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data?.results || []);
        setSubprocesses(list);
        if (list.length > 0) setActiveSpId(list[0].id);
      })
      .catch(() => {});
  }, [id]);

  const loadCategories = async () => {
    const { data } = await api.getCategories();
    setCategories(Array.isArray(data) ? data : data.results || []);
  };

  const loadCustomers = async () => {
    const { data } = await api.getCustomers();
    setCustomers(Array.isArray(data) ? data : data.results || []);
  };

  const openCategoryModal = async () => {
    const currentType = task?.category?.parent_type || '';
    setCatType(currentType);
    setCatId('');
    // Mevcut müşteri varsa pre-fill et
    setCatCustomerId(task?.customer ? String(task.customer) : '');
    setCatError('');
    setShowNewCatForm(false);
    setNewCatName('');
    setNewCatMessage('');
    await Promise.all([
      loadCategories().catch(() => {}),
      loadCustomers().catch(() => {}),
    ]);
    setShowCategoryModal(true);
  };

  const filteredCategories = useMemo(() => {
    // Reddedilen mevcut kategoriyi dropdown'dan çıkar (zaten geçersiz)
    const rejectedCatId = (task?.category && !task.category.is_active) ? task.category.id : null;
    return categories.filter(c => c.parent_type === catType && c.id !== rejectedCatId);
  }, [categories, catType, task]);

  const saveCategory = async () => {
    if (!catId) { setCatError('Kategori seçiniz.'); return; }
    if (catType === 'customer' && !catCustomerId) {
      setCatError('Müşteri İşleri için müşteri seçimi zorunludur.');
      return;
    }
    setCatSaving(true);
    setCatError('');
    try {
      const payload = { category_id: Number(catId) };
      if (catType === 'customer') {
        payload.customer = Number(catCustomerId);
      } else {
        // Genel İşler'e geçildiyse müşteriyi temizle
        payload.customer = null;
      }
      await api.updateTask(id, payload);
      const { data } = await api.getTask(id);
      setTask(data);
      setShowCategoryModal(false);
    } catch (err) {
      setCatError(err.response?.data?.detail || 'Kategori güncellenemedi.');
    } finally {
      setCatSaving(false);
    }
  };

  const submitNewCategory = async () => {
    if (!newCatName.trim()) {
      setNewCatMessageType('error');
      setNewCatMessage('Kategori adı zorunludur.');
      return;
    }
    if (!catType) {
      setNewCatMessageType('error');
      setNewCatMessage('Önce iş türü seçin.');
      return;
    }
    setNewCatSubmitting(true);
    setNewCatMessage('');
    try {
      const { data } = await api.requestCategory({ name: newCatName.trim(), parent_type: catType });
      setNewCatMessageType('success');
      setNewCatMessage('Kategori talebi gönderildi. Onay bekliyor olarak seçildi.');
      setNewCatName('');
      await loadCategories();
      if (data?.id) setCatId(String(data.id));
      setShowNewCatForm(false);
    } catch (err) {
      setNewCatMessageType('error');
      setNewCatMessage(err.response?.data?.detail || 'Kategori talebi gönderilemedi.');
    } finally {
      setNewCatSubmitting(false);
    }
  };

  const noteImagePreviews = useMemo(
    () => noteImages.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [noteImages]
  );

  useEffect(() => {
    const urls = noteImagePreviews.map((p) => p.url);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [noteImagePreviews]);

  const handleNoteImageSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const oversized = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      setNoteError(`"${oversized[0].name}" 10MB limitini aşıyor. Daha küçük bir görsel seçin.`);
      return;
    }
    setNoteError('');
    setNoteImages((prev) => {
      const combined = [...prev, ...files];
      return combined.slice(0, 5);
    });
  }, []);

  const removeNoteImageAt = useCallback((index) => {
    setNoteImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetMailNoteState = useCallback(() => {
    setShowMailInput(false);
    setMailText('');
    setMailError('');
    setMailSummarizing(false);
  }, []);

  const summarizeMail = async () => {
    if (!mailText.trim()) return;
    setMailSummarizing(true);
    setMailError('');
    try {
      const { data } = await api.summarizeMail(id, mailText.trim());
      setNoteText(typeof data?.summary === 'string' ? data.summary : '');
      setShowMailInput(false);
      setMailText('');
    } catch (err) {
      setMailError(err.response?.data?.detail || 'Özetlenemedi.');
    } finally {
      setMailSummarizing(false);
    }
  };

  const addNote = async () => {
    const trimmed = noteText.trim();
    if (!trimmed && noteImages.length === 0 && noteFiles.length === 0) return;
    setSaving(true);
    setNoteError('');
    try {
      await api.createNote(id, {
        text: trimmed || undefined,
        images: noteImages.length ? noteImages : undefined,
        files: noteFiles.length ? noteFiles : undefined,
      });
      const { data } = await api.getTask(id);
      setTask(data);
      setNoteText('');
      setNoteImages([]);
      setNoteFiles([]);
      resetMailNoteState();
      setShowNoteModal(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 413) {
        setNoteError('Dosya boyutu çok büyük. Lütfen daha küçük bir görsel deneyin.');
      } else {
        setNoteError(err.response?.data?.detail || err.response?.data?.non_field_errors?.[0] || 'Not kaydedilemedi. Lütfen tekrar deneyin.');
      }
    } finally {
      setSaving(false);
    }
  };

  const doReviseDeadline = async () => {
    if (!newDeadline || !deadlineReason.trim()) {
      setDeadlineError('Tarih ve revize nedeni zorunludur.');
      return;
    }
    setDeadlineSaving(true);
    setDeadlineError('');
    try {
      await api.reviseDeadline(id, { new_deadline: newDeadline, reason: deadlineReason.trim() });
      const { data } = await api.getTask(id);
      setTask(data);
      setShowDeadlineModal(false);
      setNewDeadline('');
      setDeadlineReason('');
    } catch (err) {
      setDeadlineError(err.response?.data?.detail || 'Revize işlemi başarısız.');
    } finally {
      setDeadlineSaving(false);
    }
  };

  const doDelegate = async () => {
    if (!delegateId) return;
    setSaving(true);
    await api.delegateTask(id, { delegate_to: delegateId, note: delegateNote });
    const { data } = await api.getTask(id);
    setTask(data);
    setShowDelegateModal(false);
    setDelegateId('');
    setDelegateNote('');
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-7 h-7 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!task) return <div className="p-4 text-gray-400">İş bulunamadı.</div>;

  const isOverdue = task.is_overdue || task.status === 'overdue';
  const isCompleted = task.status === 'completed';
  const isAdmin = user?.role === 'admin';
  const categoryRejected = task.category && !task.category.is_active;
  const canEditTitle = isAdmin || (!isCompleted && !isOverdue);

  const startEditTitle = () => {
    setEditTitle(task.title);
    setTitleError('');
    setEditingTitle(true);
  };

  const cancelEditTitle = () => {
    setEditingTitle(false);
    setTitleError('');
  };

  const saveTitle = async () => {
    const trimmed = editTitle.trim();
    if (!trimmed) { setTitleError('Başlık boş olamaz.'); return; }
    if (trimmed === task.title) { setEditingTitle(false); return; }
    setTitleSaving(true);
    setTitleError('');
    try {
      await api.renameTaskTitle(id, trimmed);
      const { data } = await api.getTask(id);
      setTask(data);
      setEditingTitle(false);
    } catch (err) {
      setTitleError(err.response?.data?.detail || 'Başlık güncellenemedi.');
    } finally {
      setTitleSaving(false);
    }
  };

  const completeSubProcess = async (spId) => {
    await api.updateSubProcess(id, spId, { status: 'completed' });
    refreshSubProcesses();
    refreshTask();
  };

  const reopenSubProcess = async (spId) => {
    await api.updateSubProcess(id, spId, { status: 'open' });
    refreshSubProcesses();
    refreshTask();
  };

  const doDeleteSubProcess = async (spId) => {
    await api.deleteSubProcess(id, spId);
    const newList = subprocesses.filter(sp => sp.id !== spId);
    setSubprocesses(newList);
    setActiveSpId(newList.length > 0 ? newList[0].id : null);
    refreshTask();
  };

  const doCreateSubProcess = async () => {
    if (!newSpTitle.trim()) { setNewSpError('Başlık zorunludur.'); return; }
    setNewSpSaving(true);
    setNewSpError('');
    try {
      await api.createSubProcess(id, {
        title: newSpTitle.trim(),
        ...(newSpDeadline ? { deadline: newSpDeadline } : {}),
      });
      const { data } = await api.getSubProcesses(id);
      const list = Array.isArray(data) ? data : (data?.results || []);
      setSubprocesses(list);
      setActiveSpId(list[list.length - 1]?.id || null);
      setNewSpTitle('');
      setNewSpDeadline('');
      setShowNewSpModal(false);
      refreshTask();
    } catch (err) {
      setNewSpError(err.response?.data?.detail || 'Oluşturulamadı.');
    } finally {
      setNewSpSaving(false);
    }
  };

  return (
    <div className="px-4 py-4 space-y-4 pb-8">

      {/* Reddedilen kategori uyarısı */}
      {categoryRejected && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-start gap-3">
          <span className="text-red-500 text-lg shrink-0">!</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-700">Bu işin kategorisi reddedildi</p>
            <p className="text-xs text-red-600 mt-0.5">
              "{task.category.name}" kategorisi admin tarafından onaylanmadı. Lütfen geçerli bir kategori seçin.
            </p>
            <button
              type="button"
              className="mt-2 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg"
              onClick={openCategoryModal}
            >
              Kategoriyi Değiştir
            </button>
          </div>
        </div>
      )}

      {/* Üst bilgi */}
      <div className="card">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex-1 min-w-0">
            {editingTitle ? (
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 shrink-0">#{task.code}</span>
                  <input
                    className="input flex-1 text-sm font-semibold py-1"
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') cancelEditTitle(); }}
                    autoFocus
                    disabled={titleSaving}
                  />
                </div>
                {titleError && <p className="text-xs text-red-500 mt-1">{titleError}</p>}
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                    onClick={saveTitle}
                    disabled={titleSaving}
                  >
                    {titleSaving ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg"
                    onClick={cancelEditTitle}
                    disabled={titleSaving}
                  >
                    İptal
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <h2 className="font-bold text-gray-900">#{task.code} {task.title}</h2>
                {canEditTitle && (
                  <button
                    type="button"
                    onClick={startEditTitle}
                    className="shrink-0 mt-0.5 text-gray-400 hover:text-brand-500 transition-colors"
                    title="Başlığı düzenle"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
          {!editingTitle && <StatusBadge status={task.status} isOverdue={isOverdue} />}
        </div>
        <p className={`text-xs font-medium mb-3 ${categoryRejected ? 'text-red-500' : 'text-brand-500'}`}>
          {task.category?.parent_type_display} / {task.category?.name}
          {categoryRejected && ' (reddedildi)'}
        </p>

        {task.customer_name && (
          <p className="text-sm text-gray-600 mb-1">
            <span className="text-gray-400">Müşteri:</span> {task.customer_name}
          </p>
        )}
        <p className="text-sm text-gray-600 mb-1">
          <span className="text-gray-400">Sorumlu:</span> {task.assignee?.full_name}
        </p>
        {(() => {
          if (!task.is_delegated) return null;
          const activeDelegate = task.delegated_to?.id !== task.assignee?.id ? task.delegated_to : null;
          if (activeDelegate) {
            return (
              <p className="text-sm text-amber-600 mb-1">
                <span className="text-gray-400">Delege olmuş:</span> {activeDelegate.full_name}
              </p>
            );
          }
          const prevDelegate = task.previous_delegated_to;
          if (prevDelegate) {
            const prevHasNotes = task.process_notes?.some(n => n.added_by === prevDelegate.id);
            if (prevHasNotes) {
              return (
                <p className="text-sm text-amber-600 mb-1">
                  <span className="text-gray-400">Delege olmuş:</span> {prevDelegate.full_name}
                </p>
              );
            }
          }
          return null;
        })()}
        <p className={`text-sm mt-2 font-medium ${isOverdue ? 'text-red-500' : 'text-gray-600'}`}>
          <span className="text-gray-400 font-normal">Deadline:</span>{' '}
          {format(new Date(task.deadline), 'd MMMM yyyy', { locale: tr })}
          {isOverdue && ' — Süresi Geçmiş!'}
        </p>

        {/* Admin: deadline revize */}
        {isAdmin && !isCompleted && (
          <button
            className="mt-2 text-xs text-brand-600 font-medium underline"
            onClick={() => {
              setNewDeadline('');
              setDeadlineReason('');
              setDeadlineError('');
              setShowDeadlineModal(true);
            }}
          >
            Deadline Revize Et
          </button>
        )}
      </div>

      {/* İş Açıklaması */}
      {task.description && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">İş Açıklaması</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{task.description}</p>
        </div>
      )}

      {/* Paralel Süreçler */}
      {(subprocesses.length > 0 || !isCompleted) && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Paralel Süreçler
              {subprocesses.length > 0 && (
                <span className="ml-2 text-xs font-normal text-gray-400">
                  {subprocesses.filter(sp => sp.status === 'completed').length}/{subprocesses.length} tamamlandı
                </span>
              )}
            </h3>
            {!isCompleted && (
              <button
                type="button"
                className="text-xs text-brand-500 font-semibold"
                onClick={() => { setNewSpTitle(''); setNewSpDeadline(''); setNewSpError(''); setShowNewSpModal(true); }}
              >
                + Yeni Süreç
              </button>
            )}
          </div>

          {subprocesses.length === 0 && (
            <p className="text-xs text-gray-400">Henüz paralel süreç eklenmedi.</p>
          )}

          {subprocesses.length > 0 && (() => {
            const activeSp = subprocesses.find(sp => sp.id === activeSpId) || null;
            return (
              <>
                {/* Sekme butonları */}
                <div className="flex gap-2 overflow-x-auto pb-1 mb-3 -mx-0.5 px-0.5">
                  {subprocesses.map((sp, i) => (
                    <button
                      key={sp.id}
                      type="button"
                      onClick={() => setActiveSpId(sp.id)}
                      className={`shrink-0 w-9 h-9 rounded-full text-xs font-bold border-2 flex items-center justify-center transition-colors
                        ${activeSpId === sp.id
                          ? 'bg-brand-500 text-white border-brand-500'
                          : sp.status === 'completed'
                            ? 'bg-green-50 text-green-600 border-green-300'
                            : 'bg-white text-gray-600 border-gray-200'
                        }`}
                    >
                      {sp.status === 'completed' ? '✓' : i + 1}
                    </button>
                  ))}
                </div>

                {/* Aktif süreç detayı */}
                {activeSp && (
                  <div className="border border-gray-100 rounded-xl p-3 bg-gray-50">
                    <p className="text-sm font-semibold text-gray-800 mb-1">{activeSp.title}</p>
                    {activeSp.deadline && (
                      <p className={`text-xs mb-2 ${
                        activeSp.status !== 'completed' && new Date(activeSp.deadline) < new Date()
                          ? 'text-red-500 font-medium'
                          : 'text-gray-500'
                      }`}>
                        Deadline: {format(new Date(activeSp.deadline), 'd MMMM yyyy', { locale: tr })}
                      </p>
                    )}
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium mb-2
                      ${activeSp.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {activeSp.status === 'completed' ? 'Tamamlandı' : 'Devam Ediyor'}
                    </span>
                    {activeSp.status === 'completed' && activeSp.completed_at && (
                      <p className="text-[11px] text-gray-400 mb-2">
                        {format(new Date(activeSp.completed_at), 'd MMM yyyy HH:mm', { locale: tr })}
                        {activeSp.completed_by_user?.full_name && ` · ${activeSp.completed_by_user.full_name}`}
                      </p>
                    )}
                    {!isCompleted && (
                      <div className="flex items-center gap-3 mt-1">
                        {activeSp.status === 'open' && (
                          <button
                            type="button"
                            onClick={() => completeSubProcess(activeSp.id)}
                            className="px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-lg"
                          >
                            Tamamlandı İşaretle
                          </button>
                        )}
                        {activeSp.status === 'completed' && (
                          <button
                            type="button"
                            onClick={() => reopenSubProcess(activeSp.id)}
                            className="text-xs text-amber-600 font-semibold"
                          >
                            ↩ Yeniden Aç
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => doDeleteSubProcess(activeSp.id)}
                            className="text-xs text-red-400 font-medium"
                          >
                            Sil
                          </button>
                        )}
                      </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Bu sürece özel notlar
                      </p>
                      {(!activeSp.process_notes || activeSp.process_notes.length === 0) && (
                        <p className="text-xs text-gray-400 mb-2">Henüz not yok.</p>
                      )}
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {(activeSp.process_notes || []).map((note) => (
                          <ProcessNote
                            key={note.id}
                            note={note}
                            canManage={user?.id === note.added_by || isAdmin}
                            canEdit={user?.id === note.added_by || isAdmin}
                            isAdmin={isAdmin}
                            onRefresh={() => { refreshSubProcesses(); refreshTask(); }}
                          />
                        ))}
                      </div>
                      {!isCompleted && activeSp.status === 'open' && (
                        <div className="mt-3 space-y-2">
                          <textarea
                            className="input min-h-[72px] resize-none text-sm"
                            placeholder="Bu paralel süreç için not yazın..."
                            value={spNoteText}
                            onChange={(e) => { setSpNoteText(e.target.value); setSpNoteError(''); }}
                            disabled={spNoteSaving}
                          />
                          {spNoteError && (
                            <p className="text-xs text-red-600">{spNoteError}</p>
                          )}
                          <button
                            type="button"
                            className="px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                            disabled={spNoteSaving || !spNoteText.trim()}
                            onClick={async () => {
                              const t = spNoteText.trim();
                              if (!t) return;
                              setSpNoteSaving(true);
                              setSpNoteError('');
                              try {
                                await api.createProcessNote(id, { text: t, subprocess: activeSp.id });
                                setSpNoteText('');
                                await refreshSubProcesses();
                                refreshTask();
                              } catch (err) {
                                setSpNoteError(
                                  err.response?.data?.detail
                                  || err.response?.data?.subprocess?.[0]
                                  || err.response?.data?.non_field_errors?.[0]
                                  || 'Not kaydedilemedi.',
                                );
                              } finally {
                                setSpNoteSaving(false);
                              }
                            }}
                          >
                            {spNoteSaving ? 'Kaydediliyor...' : 'Kaydet'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Süreç Notları */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Süreç Notları</h3>
        {(task.process_notes || []).filter((n) => !n.subprocess).length === 0 && (
          <p className="text-xs text-gray-400 mb-3">Henüz genel süreç notu yok.</p>
        )}
        <div className="space-y-3">
          {(task.process_notes || []).filter((note) => !note.subprocess).map(note => (
            <ProcessNote
              key={note.id}
              note={note}
              canManage={user?.id === note.added_by || isAdmin}
              canEdit={user?.id === note.added_by || isAdmin}
              isAdmin={isAdmin}
              onRefresh={refreshTask}
            />
          ))}
        </div>
        {!isCompleted && (
          <button
            className="mt-3 w-full border border-dashed border-gray-300 rounded-xl py-2.5 text-sm text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors"
            onClick={() => {
              setNoteImages([]);
              setNoteFiles([]);
              resetMailNoteState();
              setShowNoteModal(true);
            }}
          >
            + Süreç Notu Ekle
          </button>
        )}
      </div>

      {/* Tamamlama çıktısı (tamamlandıysa göster) */}
      {isCompleted && task.output_text && (
        <div className="card bg-green-50 border border-green-200">
          <h3 className="text-sm font-semibold text-green-700 mb-1">İş Çıktısı</h3>
          <p className="text-sm text-green-800">{task.output_text}</p>
          {task.output_type && (
            <p className="text-xs text-green-600 mt-1">Tür: {task.output_type}</p>
          )}
        </div>
      )}

      {/* Aksiyonlar */}
      {!isCompleted && (
        <div className="space-y-2">
          {subprocesses.length > 0 && subprocesses.some(sp => sp.status !== 'completed') && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              Tüm paralel süreçler tamamlanmadan iş kapatılamaz.{' '}
              ({subprocesses.filter(sp => sp.status === 'completed').length}/{subprocesses.length} tamamlandı)
            </p>
          )}
          <button
            className="btn-primary disabled:opacity-40"
            onClick={() => navigate(`/satis/tasks/${id}/complete`)}
            disabled={subprocesses.length > 0 && subprocesses.some(sp => sp.status !== 'completed')}
          >
            Tamamlandı Olarak İşaretle
          </button>
          <button className="btn-secondary" onClick={() => setShowDelegateModal(true)}>
            Delegeyi Değiştir
          </button>
          {isAdmin && (
            <button
              className="w-full text-sm text-red-500 py-2"
              onClick={() => {
                const reason = prompt('Silme nedeni:');
                if (reason) api.requestDeleteTask(id, { reason }).then(() => navigate('/satis'));
              }}
            >
              Silme Talebi Oluştur
            </button>
          )}
        </div>
      )}

      {isAdmin && (
        <button
          type="button"
          className="w-full py-3 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-sm"
          onClick={() => setShowAdminDeleteModal(true)}
        >
          İşi Sil
        </button>
      )}

      {/* Süreç Notu Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end z-[80]">
          <div className="bg-white w-full max-w-md mx-auto rounded-t-2xl px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] max-h-[90dvh] overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-3">Süreç Notu Ekle</h3>

            <div className="mb-3">
              <button
                type="button"
                onClick={() => {
                  setShowMailInput((v) => !v);
                  setMailError('');
                }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-colors
                  ${showMailInput
                    ? 'bg-gray-100 border-gray-200 text-gray-600'
                    : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                  }`}
              >
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {showMailInput ? 'Mail ile eklemeyi kapat' : 'Mail ile Ekle'}
              </button>
              {showMailInput && (
                <div className="mt-2 space-y-2">
                  <textarea
                    className="input min-h-[120px] resize-y"
                    placeholder="Mail metnini buraya yapıştırın..."
                    value={mailText}
                    onChange={(e) => setMailText(e.target.value)}
                    disabled={mailSummarizing}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl border border-brand-500 text-sm font-semibold text-brand-600 bg-brand-50 hover:bg-brand-100 disabled:opacity-50"
                    onClick={summarizeMail}
                    disabled={mailSummarizing || !mailText.trim()}
                  >
                    {mailSummarizing ? 'Özetleniyor...' : 'Özetle'}
                  </button>
                  {mailError ? (
                    <p className="text-xs text-red-600">{mailError}</p>
                  ) : null}
                </div>
              )}
            </div>

            <div className="relative mb-3">
              <textarea
                className="input min-h-[100px] resize-none pr-10"
                placeholder="Notu yazın..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                autoFocus={!showMailInput}
              />
              <button
                type="button"
                disabled
                title="Ses kaydı (Faz-2)"
                className="absolute right-3 bottom-3 w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center opacity-40"
              >
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleNoteImageSelect}
                id="gallery-input"
                className="hidden"
              />
              <label
                htmlFor="gallery-input"
                className="inline-flex items-center justify-center px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 bg-gray-50 hover:border-brand-400 hover:text-brand-600 cursor-pointer"
              >
                Görsel Ekle
              </label>
              <div className="md:hidden">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleNoteImageSelect}
                  id="camera-input"
                  className="hidden"
                />
                <label
                  htmlFor="camera-input"
                  className="inline-flex items-center justify-center px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 bg-gray-50 hover:border-brand-400 hover:text-brand-600 cursor-pointer"
                >
                  Fotoğraf Çek
                </label>
              </div>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                multiple
                onChange={e => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = '';
                  setNoteFiles(prev => [...prev, ...files].slice(0, 10));
                }}
                id="file-input"
                className="hidden"
              />
              <label
                htmlFor="file-input"
                className="inline-flex items-center justify-center px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 bg-gray-50 hover:border-brand-400 hover:text-brand-600 cursor-pointer"
              >
                Dosya Ekle
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {noteImages.length}/5 görsel
              {noteImages.length >= 5 ? ' — en fazla 5 görsel eklenebilir.' : ''}
              {noteFiles.length > 0 && ` · ${noteFiles.length} dosya`}
            </p>

            {noteImagePreviews.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {noteImagePreviews.map((p, index) => (
                  <div key={`${p.url}-${index}`} className="relative w-16 h-16 shrink-0">
                    <img
                      src={p.url}
                      alt=""
                      className="w-full h-full object-contain rounded-lg border border-gray-200 bg-gray-50"
                    />
                    <button
                      type="button"
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gray-800 text-white text-xs leading-5 font-bold"
                      onClick={() => removeNoteImageAt(index)}
                      aria-label="Görseli kaldır"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {noteFiles.length > 0 && (
              <div className="flex flex-col gap-1 mb-3">
                {noteFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
                    <svg className="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="flex-1 min-w-0 text-xs text-gray-700 truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setNoteFiles(prev => prev.filter((_, j) => j !== i))}
                      className="shrink-0 text-gray-400 hover:text-red-500"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {noteError && (
              <p className="text-xs text-red-600 mb-2">{noteError}</p>
            )}
            <button
              className="btn-primary"
              onClick={addNote}
              disabled={saving || (!noteText.trim() && noteImages.length === 0 && noteFiles.length === 0)}
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
            <button
              type="button"
              className="btn-secondary mt-2"
              onClick={() => {
                setShowNoteModal(false);
                setNoteError('');
                setNoteImages([]);
                setNoteFiles([]);
                resetMailNoteState();
              }}
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Delegasyon Modal */}
      {showDelegateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end z-[80]" onClick={() => setShowDelegateModal(false)}>
          <div className="bg-white w-full max-w-md mx-auto rounded-t-2xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-3">Delegeyi Değiştir</h3>
            <select
              className="input mb-2"
              value={delegateId}
              onChange={e => setDelegateId(e.target.value)}
            >
              <option value="">Kişi seçin</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.full_name}{u.is_on_leave ? ' (İzinde)' : ''}
                </option>
              ))}
            </select>
            <input
              className="input mb-3"
              placeholder="Delegasyon nedeni (ör: iş yoğunluğu, izin)"
              value={delegateNote}
              onChange={e => setDelegateNote(e.target.value)}
            />
            <button className="btn-primary" onClick={doDelegate} disabled={saving || !delegateId}>
              {saving ? 'Kaydediliyor...' : 'Delege Et'}
            </button>
            <button className="btn-secondary mt-2" onClick={() => setShowDelegateModal(false)}>İptal</button>
          </div>
        </div>
      )}

      {/* Deadline Revize Modal */}
      {showDeadlineModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end z-[80]" onClick={() => setShowDeadlineModal(false)}>
          <div className="bg-white w-full max-w-md mx-auto rounded-t-2xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-1">Deadline Revize Et</h3>
            <p className="text-xs text-gray-500 mb-3">
              Mevcut deadline:{' '}
              <span className="font-medium text-gray-700">
                {task?.deadline ? format(new Date(task.deadline), 'd MMMM yyyy', { locale: tr }) : '—'}
              </span>
            </p>

            <label className="text-xs font-medium text-gray-600 block mb-1">
              Yeni Deadline <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className="input mb-1"
              value={newDeadline}
              min={new Date().toISOString().split('T')[0]}
              onChange={e => setNewDeadline(e.target.value)}
            />
            {newDeadline && (
              <p className="text-xs text-brand-600 mb-3">
                {format(new Date(newDeadline), 'd MMMM yyyy', { locale: tr })}
              </p>
            )}

            <label className="text-xs font-medium text-gray-600 block mb-1">
              Revize Nedeni <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input min-h-[80px] resize-none mb-3"
              placeholder="Örn: Müşteri geri dönmedi, süreç devam ediyor..."
              value={deadlineReason}
              onChange={e => setDeadlineReason(e.target.value)}
            />

            {deadlineError && (
              <p className="text-xs text-red-600 mb-2">{deadlineError}</p>
            )}

            <button
              className="btn-primary"
              onClick={doReviseDeadline}
              disabled={deadlineSaving || !newDeadline || !deadlineReason.trim()}
            >
              {deadlineSaving ? 'Kaydediliyor...' : 'Revize Et'}
            </button>
            <button className="btn-secondary mt-2" onClick={() => setShowDeadlineModal(false)}>
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Yönetici iş silme onayı */}
      {showAdminDeleteModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end z-[80]"
          onClick={() => !adminDeleteSaving && setShowAdminDeleteModal(false)}
        >
          <div
            className="bg-white w-full max-w-md mx-auto rounded-t-2xl p-4"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold text-gray-900 mb-2">İşi sil</h3>
            <p className="text-sm text-gray-600 mb-4">
              Bu işi silmek istediğinizden emin misiniz? İlgili kişilere bildirim gönderilecektir.
            </p>
            <button
              type="button"
              className="w-full py-3 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              disabled={adminDeleteSaving}
              onClick={async () => {
                setAdminDeleteSaving(true);
                try {
                  await api.adminDeleteTask(task.id);
                  navigate('/satis');
                } catch {
                  setAdminDeleteSaving(false);
                }
              }}
            >
              {adminDeleteSaving ? 'Siliniyor...' : 'Evet, sil'}
            </button>
            <button
              type="button"
              className="btn-secondary mt-2 w-full"
              disabled={adminDeleteSaving}
              onClick={() => setShowAdminDeleteModal(false)}
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Yeni Paralel Süreç Modal */}
      {showNewSpModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end z-[80]" onClick={() => setShowNewSpModal(false)}>
          <div className="bg-white w-full max-w-md mx-auto rounded-t-2xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-3">Yeni Paralel Süreç</h3>
            <input
              className="input mb-2"
              placeholder="Süreç başlığı *"
              value={newSpTitle}
              onChange={e => setNewSpTitle(e.target.value)}
              autoFocus
            />
            <label className="text-xs font-medium text-gray-600 block mb-1">Deadline (opsiyonel)</label>
            <input
              type="date"
              className="input mb-3"
              value={newSpDeadline}
              onChange={e => setNewSpDeadline(e.target.value)}
            />
            {newSpError && <p className="text-xs text-red-600 mb-2">{newSpError}</p>}
            <button
              className="btn-primary disabled:opacity-50"
              onClick={doCreateSubProcess}
              disabled={newSpSaving || !newSpTitle.trim()}
            >
              {newSpSaving ? 'Oluşturuluyor...' : 'Oluştur'}
            </button>
            <button type="button" className="btn-secondary mt-2" onClick={() => setShowNewSpModal(false)}>
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Kategori Değiştirme Modal */}
      {showCategoryModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end z-[80]"
          onClick={() => setShowCategoryModal(false)}
        >
          <div
            className="bg-white w-full max-w-md mx-auto rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold text-gray-900 mb-1">Kategoriyi Değiştir</h3>
            <p className="text-xs text-gray-500 mb-3">
              Mevcut kategori reddedildi. Lütfen geçerli bir kategori seçin veya yeni talep oluşturun.
            </p>

            {/* İş Türü seçimi */}
            <p className="text-xs font-medium text-gray-600 mb-1">İş Türü</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { value: 'general', label: 'Genel İşler' },
                { value: 'customer', label: 'Müşteri İşleri' },
              ].map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => { setCatType(type.value); setCatId(''); setCatCustomerId(''); }}
                  className={`py-2.5 px-3 rounded-xl border text-sm font-medium transition-colors
                    ${catType === type.value
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-white text-gray-600 border-gray-200'
                    }`}
                >
                  {type.label}
                </button>
              ))}
            </div>

            {/* Kategori dropdown */}
            {catType && (
              <>
                <p className="text-xs font-medium text-gray-600 mb-1">Kategori</p>
                <select
                  className="input mb-1"
                  value={catId}
                  onChange={e => setCatId(e.target.value)}
                >
                  <option value="">Kategori seçin</option>
                  {filteredCategories.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{!c.is_active ? ' (onay bekliyor)' : ''}
                    </option>
                  ))}
                </select>
                {filteredCategories.length === 0 && (
                  <p className="text-xs text-amber-700 mb-1">Bu türde aktif kategori yok.</p>
                )}

                {/* Müşteri İşleri seçildiyse müşteri zorunlu */}
                {catType === 'customer' && (
                  <div className="mt-3 mb-1">
                    <p className="text-xs font-medium text-gray-600 mb-1">
                      Müşteri <span className="text-red-500">*</span>
                    </p>
                    <select
                      className="input"
                      value={catCustomerId}
                      onChange={e => setCatCustomerId(e.target.value)}
                    >
                      <option value="">Müşteri seçin</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {!catCustomerId && (
                      <p className="text-xs text-amber-700 mt-0.5">
                        Müşteri İşleri için müşteri seçimi zorunludur.
                      </p>
                    )}
                  </div>
                )}

                {/* Yeni kategori talep alt formu */}
                {!showNewCatForm ? (
                  <button
                    type="button"
                    className="text-xs font-semibold text-brand-600 mb-3"
                    onClick={() => { setShowNewCatForm(true); setNewCatName(''); setNewCatMessage(''); }}
                  >
                    + Yeni Kategori Talep Et
                  </button>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-3">
                    <p className="text-xs font-semibold text-gray-700 mb-1">Yeni Kategori Talebi</p>
                    <input
                      className="input text-sm mb-1"
                      placeholder="Kategori adı"
                      value={newCatName}
                      onChange={e => setNewCatName(e.target.value)}
                      autoFocus
                    />
                    {newCatMessage && (
                      <p className={`text-xs mt-1 ${newCatMessageType === 'error' ? 'text-red-600' : 'text-green-600'}`}>
                        {newCatMessage}
                      </p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                        onClick={submitNewCategory}
                        disabled={newCatSubmitting || !newCatName.trim()}
                      >
                        {newCatSubmitting ? 'Gönderiliyor...' : 'Talep Gönder'}
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg"
                        onClick={() => setShowNewCatForm(false)}
                      >
                        İptal
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {catError && (
              <p className="text-xs text-red-600 mb-2">{catError}</p>
            )}

            <button
              className="btn-primary"
              onClick={saveCategory}
              disabled={catSaving || !catId}
            >
              {catSaving ? 'Kaydediliyor...' : 'Kategoriyi Güncelle'}
            </button>
            <button
              className="btn-secondary mt-2"
              onClick={() => setShowCategoryModal(false)}
            >
              İptal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
